import { useState, useEffect, useRef } from 'react'
import { clientApi } from '../../services/clientApi'
import type { IoTStatus, SyncDevicesResult } from '../../types/client'

const REFRESH_INTERVAL = 30_000

interface Props {
  clientId: string
  onClose?: () => void
}

export default function ClientIoTStatus({ clientId, onClose }: Props) {
  const [status, setStatus] = useState<IoTStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<SyncDevicesResult | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchStatus = async () => {
    try {
      const data = await clientApi.getIoTStatus(clientId)
      setStatus(data)
    } catch {
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    fetchStatus()
    intervalRef.current = setInterval(fetchStatus, REFRESH_INTERVAL)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [clientId])

  const handleSync = async () => {
    setSyncing(true)
    setSyncResult(null)
    try {
      const result = await clientApi.syncDevices(clientId)
      setSyncResult(result)
      await fetchStatus()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur synchronisation')
    } finally {
      setSyncing(false)
    }
  }

  if (loading) return <p className="admin-loading">Chargement statut IoT...</p>

  return (
    <div className="admin-iot-panel">
      <div className="admin-iot-status">
        <div className="admin-iot-status__row">
          <span>Connexion</span>
          <span className={`admin-badge ${status?.connected ? 'admin-badge--green' : 'admin-badge--red'}`}>
            {status?.connected ? 'Connecte' : 'Deconnecte'}
          </span>
        </div>
        <div className="admin-iot-status__row">
          <span>Provider</span>
          <span>{status?.provider_type ?? 'N/A'}</span>
        </div>
        {status?.customer_id && (
          <div className="admin-iot-status__row">
            <span>Customer ID</span>
            <span>{status.customer_id.slice(0, 8)}...{status.customer_id.slice(-4)}</span>
          </div>
        )}
        <div className="admin-iot-status__row">
          <span>Devices synchronises</span>
          <span>{status?.device_count ?? 0}</span>
        </div>
        {status?.last_telemetry && (
          <div className="admin-iot-status__row">
            <span>Derniere telemetrie</span>
            <span>{new Date(status.last_telemetry).toLocaleString()}</span>
          </div>
        )}
      </div>

      <div className="admin-form__actions">
        {onClose && <button className="admin-btn" onClick={onClose}>Fermer</button>}
        <button className="admin-btn admin-btn--create" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Synchronisation...' : 'Synchroniser devices'}
        </button>
      </div>

      {syncResult && (
        <div className="admin-success">
          {syncResult.created} badges crees, {syncResult.updated} mis a jour
        </div>
      )}
    </div>
  )
}
